/**
 * <iz-bundle>: "Complete the Ritual" box on the product page.
 *
 * - Ticking items swaps in the total for that combination. Totals are rendered by Liquid for every
 *   combination (data-totals), so they use the store's own money format in every language and currency.
 * - The button adds the ticked items in one request and announces it with the same standard cart event
 *   Horizon's add-to-cart uses, so the cart drawer refreshes and opens. Without a drawer it opens the cart page.
 * - When the shopper picks another variant in the buy box, the box re-renders for that variant.
 * - Products already in the box are hidden from "Frequently Bought Together" so the page doesn't repeat itself.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

class IzBundle extends HTMLElement {
  #abort = new AbortController();
  #observer = new MutationObserver(() => this.#hideRepeats());

  connectedCallback() {
    const { signal } = this.#abort;
    document.querySelectorAll('product-recommendations').forEach((el) => this.#observer.observe(el, { childList: true, subtree: true }));
    this.#hideRepeats();
    this.addEventListener('change', () => this.updateTotal(), { signal });
    this.querySelector('[data-add]')?.addEventListener('click', () => this.addToCart(), { signal });
    document.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#observer.disconnect();
  }

  #hideRepeats() {
    const ids = new Set([...this.querySelectorAll('[data-product-id]')].map((el) => el.dataset.productId));
    for (const recs of document.querySelectorAll('product-recommendations')) {
      const cards = [...recs.querySelectorAll('product-card')];
      if (!cards.length) continue;
      let shown = 0;
      for (const card of cards) {
        const item = card.closest('.resource-list__item, .resource-list__slide') || card;
        const repeat = ids.has(card.dataset.productId);
        item.style.display = repeat ? 'none' : '';
        if (!repeat) shown += 1;
      }
      const section = recs.closest('.shopify-section');
      if (section) section.style.display = shown ? '' : 'none';
    }
  }

  get #optional() {
    return [...this.querySelectorAll('input[data-optional]')];
  }

  updateTotal() {
    const mask = this.#optional.reduce((bits, box, i) => (box.checked ? bits | (1 << i) : bits), 0);
    const totals = JSON.parse(this.dataset.totals || '{}');
    const total = this.querySelector('[data-total]');
    if (total && totals[mask]) total.textContent = totals[mask];
  }

  /** @param {Event & { promise?: Promise<any> }} event */
  #onProductSelect = (event) => {
    if (event.target instanceof Element && event.target.closest('product-card')) return;
    event.promise
      ?.then(({ detail }) => {
        const id = detail?.resource?.id;
        if (id && String(id) !== this.dataset.variantId) this.#rerender(id);
      })
      .catch(() => {});
  };

  async #rerender(variantId) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    url.searchParams.set('section_id', this.dataset.sectionId);
    const html = await fetch(url).then((response) => response.text());
    const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('iz-bundle');
    if (fresh) this.replaceWith(document.importNode(fresh, true));
  }

  async addToCart() {
    const button = this.querySelector('[data-add]');
    const error = this.querySelector('.iz-bundle__error');
    const ids = [...this.querySelectorAll('input[data-variant-id]')].filter((box) => box.checked).map((box) => box.dataset.variantId);
    if (!ids.length) return;

    const drawer = document.querySelector('cart-drawer-component');
    const sectionIds = [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();

    button.disabled = true;
    error.hidden = true;
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: ids.map((id) => ({ merchandiseId: id, quantity: 1 })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(this.dataset.addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: ids.map((id) => ({ id: Number(id), quantity: 1 })), sections: sectionIds.join(',') }),
      });
      const added = await response.json();
      if (!response.ok || added.status) throw new Error(added.description || added.message || 'Add to cart failed');

      const cart = await fetch(`${this.dataset.cartUrl}.js`).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'iz-bundle', itemCount: ids.length, sections: added.sections, didError: false },
      });
      if (!drawer) window.location.href = this.dataset.cartUrl;
    } catch (e) {
      deferred.resolve({ detail: { didError: true, source: 'iz-bundle' } });
      error.hidden = false;
    } finally {
      button.disabled = false;
    }
  }
}

if (!customElements.get('iz-bundle')) customElements.define('iz-bundle', IzBundle);
